'use client'

import { useEffect } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'

/**
 * First-run guided tour for the Radar dashboard.
 *
 * Mounted inside the authenticated dashboard render (the access gate uses
 * early returns, so by the time this mounts the target elements exist).
 * Runs once per browser, gated by a localStorage flag, and is skippable
 * (Esc / outside click / close button). Steps target stable data-tour
 * hooks rather than fragile CSS selectors.
 *
 * Targets present at first load only: the domain input, the Run Audit
 * button, and the floating advisor. Score and action-item steps are
 * intentionally omitted because those panels mount only after an audit
 * runs; a post-audit tour can be added later if wanted.
 */
const TOUR_FLAG = 'pm_radar_tour_seen'

const STEPS = [
  {
    element: '[data-tour="domain"]',
    popover: {
      title: 'Start here',
      description:
        'Enter any domain to audit. Radar runs all 13 AI visibility tools on it in a single pass.',
    },
  },
  {
    element: '[data-tour="run"]',
    popover: {
      title: 'Run the audit',
      description:
        'One click runs every selected tool. In seconds you get a unified score, cross-tool insights, and prioritized fixes.',
    },
  },
  {
    element: '[data-tour="advisor"]',
    popover: {
      title: 'Ask the advisor',
      description:
        'After an audit, ask the AI advisor what to fix first and why. It answers with your audit results in context.',
    },
  },
]

export default function RadarTour() {
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      if (localStorage.getItem(TOUR_FLAG) === 'true') return
    } catch {
      return
    }
    // Small delay so layout settles before driver measures element positions.
    const timer = window.setTimeout(() => {
      if (!document.querySelector('[data-tour="domain"]')) return
      const tour = driver({
        showProgress: true,
        allowClose: true,
        popoverClass: 'pm-radar-tour',
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: 'Got it',
        steps: STEPS,
        onDestroyed: () => {
          try {
            localStorage.setItem(TOUR_FLAG, 'true')
          } catch {
            /* localStorage disabled — tour simply shows again next visit */
          }
        },
      })
      tour.drive()
    }, 400)
    return () => window.clearTimeout(timer)
  }, [])

  // Dark, on-brand popover styling. Children-as-text <style> is the approved
  // alternative to dangerouslySetInnerHTML (see CLAUDE.md). Sharp corners come
  // for free from the global border-radius reset.
  return (
    <style>{`
      .driver-popover.pm-radar-tour {
        background: var(--background);
        color: var(--foreground);
        border: 1px solid var(--border);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
      }
      .driver-popover.pm-radar-tour .driver-popover-title,
      .driver-popover.pm-radar-tour .driver-popover-description {
        color: var(--foreground);
      }
      .driver-popover.pm-radar-tour .driver-popover-progress-text {
        color: var(--foreground);
        opacity: 0.6;
      }
      .driver-popover.pm-radar-tour button.driver-popover-next-btn {
        background: #008dcc;
        color: #fff;
        border: none;
        text-shadow: none;
        font-weight: 600;
      }
      .driver-popover.pm-radar-tour button.driver-popover-prev-btn {
        background: transparent;
        color: var(--foreground);
        border: 1px solid var(--border);
        text-shadow: none;
      }
    `}</style>
  )
}
